/**
 * Debug component to test and visualize captured heatmap snapshots
 * Use this to verify that heatmaps are being captured correctly before sending to Carbone
 */
import { FC, useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, CircularProgress, Alert, Grid } from "@mui/material";
import { captureAllHeatmaps } from "utils/carbone/svgToBase64";

// Type for captured heatmaps (matches return type of captureAllHeatmaps)
interface CapturedHeatmaps {
  luminance: Record<string, string | null>;
  illuminance: string | null;
  illuminanceLeft: string | null;
  illuminanceRight: string | null;
  borderLeft: string | null;
  borderRight: string | null;
}

interface HeatmapDebugModalProps {
  open: boolean;
  onClose: () => void;
  luminanceKeys: string[];
  hasBorderLeft: boolean;
  hasBorderRight: boolean;
}

interface HeatmapPreviewProps {
  title: string;
  base64: string | null;
}

const HeatmapPreview: FC<HeatmapPreviewProps> = ({ title, base64 }) => (
  <Box sx={{ mb: 2, p: 1, border: "1px solid #ccc", borderRadius: 1 }}>
    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
      {title}
    </Typography>
    {base64 ? (
      <Box>
        <img src={base64} alt={title} style={{ maxWidth: "100%", border: "1px solid #eee" }} />
        <Typography variant="caption" color="success.main" display="block">
          ✓ Captured ({Math.round(base64.length / 1024)} KB)
        </Typography>
      </Box>
    ) : (
      <Alert severity="warning" sx={{ py: 0.5 }}>
        Not captured - div not found or empty
      </Alert>
    )}
  </Box>
);

const HeatmapDebugModal: FC<HeatmapDebugModalProps> = ({ open, onClose, luminanceKeys, hasBorderLeft, hasBorderRight }) => {
  const [loading, setLoading] = useState(false);
  const [heatmaps, setHeatmaps] = useState<CapturedHeatmaps | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [domInfo, setDomInfo] = useState<string[]>([]);

  const handleCapture = async () => {
    setLoading(true);
    setError(null);
    setHeatmaps(null);

    // First, scan the DOM for heatmap divs
    const foundDivs: string[] = [];
    document.querySelectorAll('[id*="heatmaproot"]').forEach((el) => {
      const hasSvg = !!el.querySelector("svg");
      foundDivs.push(`${el.id} (SVG: ${hasSvg ? "YES" : "NO"})`);
    });
    setDomInfo(foundDivs);

    try {
      // Add a small delay to ensure D3 has finished rendering
      await new Promise((resolve) => setTimeout(resolve, 300));

      const captured = await captureAllHeatmaps(luminanceKeys, hasBorderLeft, hasBorderRight);
      setHeatmaps(captured);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to capture heatmaps");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Heatmap Capture Debug</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Button variant="contained" onClick={handleCapture} disabled={loading}>
            {loading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
            {loading ? "Capturing..." : "Capture Heatmaps Now"}
          </Button>
        </Box>

        {domInfo.length > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="subtitle2">Found heatmap divs in DOM:</Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {domInfo.map((info, i) => (
                <li key={i}>
                  <code>{info}</code>
                </li>
              ))}
            </ul>
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {heatmaps && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Luminance Heatmaps
              </Typography>
              {Object.entries(heatmaps.luminance).map(([key, base64]) => (
                <HeatmapPreview key={key} title={`Lane: ${key}`} base64={base64} />
              ))}
              {Object.keys(heatmaps.luminance).length === 0 && <Alert severity="warning">No luminance heatmaps captured</Alert>}
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Illuminance Heatmaps
              </Typography>
              <HeatmapPreview title="Main Illuminance" base64={heatmaps.illuminance} />
              <HeatmapPreview title="Border Left" base64={heatmaps.borderLeft} />
              <HeatmapPreview title="Border Right" base64={heatmaps.borderRight} />
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default HeatmapDebugModal;
